import React, {
  useState, useEffect, useRef, Suspense, lazy,
} from 'react';
import { useQuery } from '@apollo/client';
import {
  Header, Button, Form, Input, Icon, Loader, Modal,
} from 'semantic-ui-react';
import Loading from '../../Loading';
import ProjectShareModal from '../ProjectShareModal';
import { PROJECT_METADATA } from '../../../queries/project';
import { JOBS_PAGE } from '../../../queries/job';
import NoProjectJobs from './NoProjectJobs';
import ProjectJobs from './ProjectJobs';
import SearchJobs from './SearchJobs';
import ExportExpertButton from './ExportExpertButton';

const TrackProjectProgress = lazy(() => import('../TrackProjectProgres'));

const PER_PAGE = 20;

const ManageProjectJobs = ({ project: basicProject }) => {
  const [query, setQuery] = useState('');
  const [submittedSearchQuery, setSubmittedSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [allJobs, setAllJobs] = useState([]);
  const [hasMoreJobs, setHasMoreJobs] = useState(true);
  const scrollSentinelRef = useRef(null);

  const { loading: projectLoading, data: projectData } = useQuery(PROJECT_METADATA, {
    variables: { id: basicProject.id },
  });

  const { data: jobsPageData, loading: jobsPageLoading } = useQuery(JOBS_PAGE, {
    variables: {
      filters: { project: basicProject.id },
      pagination: { page, per_page: PER_PAGE },
      sortBy: { groupByStatus: true },
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!jobsPageData?.jobs) return;
    const fetchedJobs = jobsPageData.jobs;
    setAllJobs((prev) => {
      const jobsById = new Map(prev.map((job) => [job.id, job]));
      fetchedJobs.forEach((job) => {
        jobsById.set(job.id, job);
      });
      return Array.from(jobsById.values());
    });
    setHasMoreJobs(fetchedJobs.length === PER_PAGE);
  }, [jobsPageData]);

  // Infinite scroll: fetch the next page only once the sentinel below the
  // job list actually scrolls into view. The `groupByStatus` sort above
  // orders jobs server-side (accepted/upcoming first, then completed, then
  // pending, then everything else), so each page already arrives in its
  // final display position - no client-side re-sorting needed.
  useEffect(() => {
    const sentinel = scrollSentinelRef.current;
    if (!sentinel || submittedSearchQuery || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMoreJobs && !jobsPageLoading) {
        setPage((prev) => prev + 1);
      }
    }, { rootMargin: '150px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreJobs, jobsPageLoading, submittedSearchQuery]);

  if (projectLoading || (allJobs.length === 0 && jobsPageLoading)) return <Loading />;

  const project = { ...projectData.project, jobs: allJobs };

  const projectLinkButton = (
    <ProjectShareModal project={project}>
      <Button className="basic-btn">Invite my contacts</Button>
    </ProjectShareModal>
  );

  const toolbarButtons = (
    <>
      <Modal
        closeIcon
        size="large"
        trigger={<Button className="basic-btn">Track Progress</Button>}
      >
        <Modal.Content>
          <Suspense fallback={<Loading />}>
            <TrackProjectProgress project={project} />
          </Suspense>
        </Modal.Content>
      </Modal>
      <ExportExpertButton projectId={project.id} />
      {projectLinkButton}
    </>
  );

  return (
    <div id="manage-project-jobs">
      <div className="search-section">
        <Form onSubmit={() => setSubmittedSearchQuery(query)}>
          <Input
            placeholder="Search engagements"
            label={(<Button icon><Icon name="search" /></Button>)}
            labelPosition="right"
            onChange={(e, { value }) => setQuery(value)}
          />
        </Form>
      </div>
      {submittedSearchQuery ? <SearchJobs project={project} query={submittedSearchQuery} /> : (
        <div>
          <div className="top-share-section">
            {toolbarButtons}
          </div>
          {
            project.jobs.length === 0 && !jobsPageLoading
              ? <NoProjectJobs project={project} />
              : <ProjectJobs project={project} />
            }
          <div ref={scrollSentinelRef} />
          {hasMoreJobs && jobsPageLoading && allJobs.length > 0 && (
            <Loader active inline="centered" className="jobs-load-more" />
          )}
          <div className="bottom-share-section">
            <Header size="large">
              Got experts?
            </Header>
            <div className="description">
              <Header.Subheader>
                Invite your own contacts to the project.
              </Header.Subheader>
              <Header.Subheader>
                Dexter will handle everything else - screening, scheduling, and payments
              </Header.Subheader>
            </div>
            {projectLinkButton}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProjectJobs;
